/**
 * Shared OGC URL normalization for GeoServer-style endpoints.
 *
 * Admins often paste full GetCapabilities / GetMap / GetFeature URLs. ArcGIS
 * layer classes rebuild those requests themselves, so we strip OGC verbs and
 * keep vendor auth params (e.g. authkey) as `customParameters`.
 */

/** Params ArcGIS (or the layer class) will re-add; safe to drop from pasted URLs. */
const OGC_REQUEST_PARAMS = new Set([
  'service',
  'version',
  'request',
  'typename',
  'typenames',
  'layers',
  'layer',
  'outputformat',
  'srsname',
  'crs',
  'bbox',
  'filter',
  'cql_filter',
  'resulttype',
  'count',
  'startindex',
  'sortby',
  'propertyname',
  'transparent',
  'format',
  'width',
  'height',
  'styles',
  'tiled',
]);

export type ParsedOgcServiceUrl = {
  /** Endpoint with OGC request query params removed. */
  url: string;
  /** Non-OGC params (e.g. authkey) for ArcGIS `customParameters`. */
  customParameters?: Record<string, string>;
  /** Feature type from a GetFeature URL, if present. */
  featureTypeName?: string;
  /** Layer name from a WMS URL `layers=` param, if present. */
  wmsLayerName?: string;
};

/**
 * Normalize a pasted OGC endpoint for ArcGIS WFSLayer / WMSLayer.
 */
export function parseOgcServiceUrl(rawUrl: string): ParsedOgcServiceUrl {
  if (!rawUrl) {
    return { url: rawUrl };
  }

  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return { url: rawUrl };
  }

  const featureTypeName =
    parsed.searchParams.get('typeNames') ||
    parsed.searchParams.get('typenames') ||
    parsed.searchParams.get('typeName') ||
    parsed.searchParams.get('typename') ||
    undefined;

  // WMS may list several layers; take the first for companion derivation.
  const layersParam = parsed.searchParams.get('layers');
  const wmsLayerName = layersParam?.split(',')[0]?.trim() || undefined;

  const customParameters: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    if (!OGC_REQUEST_PARAMS.has(key.toLowerCase())) {
      customParameters[key] = value;
    }
  });

  parsed.search = '';
  parsed.hash = '';

  return {
    url: parsed.toString().replace(/\/$/, ''),
    ...(Object.keys(customParameters).length > 0 ? { customParameters } : {}),
    ...(featureTypeName ? { featureTypeName } : {}),
    ...(wmsLayerName ? { wmsLayerName } : {}),
  };
}
